/**
 * Mandatsauflösung (P6): the Auflösungsvorgang's vocabularies, its state machine, the Enddatum rule
 * and the DTOs the api and the web share. Pure, no I/O (rule #2: the rules live here and in NestJS,
 * never in Postgres; the DB CHECKs of 0193 pin the same closed sets).
 *
 * Dates are Swiss calendar days as `YYYY-MM-DD`. An Enddatum is inclusive: the mandate ends at the end
 * of that day (Stefan #15), so the customer turns inaktiv on the day after.
 */

// ── vocabularies ───────────────────────────────────────────────────────────────────────────────

export const TERMINATION_STATUS = {
  EROEFFNET: 'eroeffnet',
  IN_PRUEFUNG: 'in_pruefung',
  FREIGEGEBEN: 'freigegeben',
  WIRKSAM: 'wirksam',
  WIDERRUFEN: 'widerrufen',
  ZURUECKGEWIESEN: 'zurueckgewiesen',
  ABGESCHLOSSEN: 'abgeschlossen',
} as const;
export type TerminationStatus = (typeof TERMINATION_STATUS)[keyof typeof TERMINATION_STATUS];
export const TERMINATION_STATUS_VALUES = Object.values(TERMINATION_STATUS) as TerminationStatus[];

/** The statuses that keep a termination live (one per customer, 0193 uq_mandate_termination_live). */
export const TERMINATION_LIVE_STATUSES: readonly TerminationStatus[] = ['eroeffnet', 'in_pruefung', 'freigegeben', 'wirksam'];

/** The statuses a Freigabe or a Zurückweisung may start from. */
export const TERMINATION_DECIDABLE_STATUSES: readonly TerminationStatus[] = ['eroeffnet', 'in_pruefung'];

/** The termination state machine (Kap. 10.2). Exhaustive; terminal states have no way out. */
export const LEGAL_TERMINATION_TRANSITIONS = {
  eroeffnet: ['in_pruefung', 'freigegeben', 'zurueckgewiesen'],
  // A Befund leaves by «Berechtigung bestätigt» (back to eroeffnet, then the Freigabe) or by the Zurückweisung;
  // the Freigabe itself refuses in_pruefung (409 termination_check_open).
  in_pruefung: ['eroeffnet', 'zurueckgewiesen'],
  freigegeben: ['wirksam'],
  wirksam: ['widerrufen', 'abgeschlossen'],
  widerrufen: [],
  zurueckgewiesen: [],
  abgeschlossen: [],
} as const satisfies Record<TerminationStatus, readonly TerminationStatus[]>;

export function isLegalTerminationTransition(from: TerminationStatus, to: TerminationStatus): boolean {
  return (LEGAL_TERMINATION_TRANSITIONS[from] as readonly TerminationStatus[]).includes(to);
}

export const TERMINATION_STATUS_LABEL: Record<TerminationStatus, string> = {
  eroeffnet: 'eröffnet',
  in_pruefung: 'in Prüfung',
  freigegeben: 'freigegeben',
  wirksam: 'wirksam',
  widerrufen: 'widerrufen',
  zurueckgewiesen: 'zurückgewiesen',
  abgeschlossen: 'abgeschlossen',
};

export function terminationStatusLabel(status: TerminationStatus): string {
  return TERMINATION_STATUS_LABEL[status];
}

export const TERMINATION_TRIGGER = { KUNDE_EINGANG: 'kunde_eingang', KUNDE_PORTAL: 'kunde_portal', MANUELL: 'manuell' } as const;
export type TerminationTrigger = (typeof TERMINATION_TRIGGER)[keyof typeof TERMINATION_TRIGGER];

export const TERMINATION_CHANNEL_VALUES = ['email', 'post', 'upload', 'scan', 'portal', 'ruecklauf', 'manuell'] as const;
export type TerminationChannel = (typeof TERMINATION_CHANNEL_VALUES)[number];

export const TERMINATION_ART_VALUES = ['sofort', 'stichtag'] as const;
export type TerminationArt = (typeof TERMINATION_ART_VALUES)[number];

export const END_DATE_SOURCE_VALUES = ['dokument', 'erfasst', 'abgeleitet', 'rueckdatiert'] as const;
export type EndDateSource = (typeof END_DATE_SOURCE_VALUES)[number];

export const TERMINATION_REASON_CODES = [
  'kuendigung_telefonisch',
  'kuendigung_schriftlich',
  'kuendigung_email',
  'niederlegung_broker',
  'analyse_ohne_folgemandat',
  'sonstiges',
] as const;
export type TerminationReasonCode = (typeof TERMINATION_REASON_CODES)[number];

/** The reasons staff may pick in the manual dialog (kuendigung_email belongs to the intake of S2). */
export const MANUAL_TERMINATION_REASON_CODES: readonly TerminationReasonCode[] = [
  'kuendigung_telefonisch',
  'kuendigung_schriftlich',
  'niederlegung_broker',
  'analyse_ohne_folgemandat',
  'sonstiges',
];

export const TERMINATION_REASON_LABEL: Record<TerminationReasonCode, string> = {
  kuendigung_telefonisch: 'Kündigung telefonisch',
  kuendigung_schriftlich: 'Kündigung schriftlich',
  kuendigung_email: 'Kündigung per E-Mail',
  niederlegung_broker: 'Mandatsniederlegung durch den Broker',
  analyse_ohne_folgemandat: 'Analyse ohne Folgemandat',
  sonstiges: 'Sonstiges',
};

/** The remark (Pflichtbegründung) is bounded like every free text (0193 CHECK). */
export const TERMINATION_REMARK_MAX = 2000;

// ── settings (17.3) ────────────────────────────────────────────────────────────────────────────

export const FRISTREGEL_VALUES = ['sofort', 'monatsende', 'quartalsende', 'x_tage'] as const;
export type Fristregel = (typeof FRISTREGEL_VALUES)[number];

/**
 * An Eingangsdatum may lie at most this many calendar days before the Zurich
 * today unless the caller holds customer_status.override (the backdate key); older is 400 received_on_too_old.
 */
export const RECEIVED_ON_MAX_AGE_DAYS = 30;

export const MANDATE_TERMINATION_CONFIG_BOUNDS = {
  fristregelTage: { min: 1, max: 90 },
  uebergabeTageVorEnddatum: { min: 0, max: 30 },
  portalNachlaufTage: { min: 0, max: 365 },
  aufbewahrungJahre: { min: 1, max: 30 },
} as const;

export interface MandateTerminationConfigDto {
  fristregel: Fristregel;
  fristregelTage: number;
  uebergabeTageVorEnddatum: number;
  portalNachlaufTage: number;
  aufbewahrungJahre: number;
  istDefault: boolean;
}

export const MANDATE_TERMINATION_CONFIG_DEFAULTS: MandateTerminationConfigDto = {
  fristregel: 'monatsende',
  fristregelTage: 30,
  uebergabeTageVorEnddatum: 10,
  portalNachlaufTage: 90,
  aufbewahrungJahre: 10,
  istDefault: true,
};

// ── calendar helpers (Swiss calendar days as YYYY-MM-DD, computed on UTC dates: no time of day) ─

function parseIsoDay(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map((s) => parseInt(s, 10));
  return new Date(Date.UTC(y!, m! - 1, d!));
}

function toIsoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** `YYYY-MM-DD` plus n calendar days. */
export function addDaysIso(iso: string, days: number): string {
  const d = parseIsoDay(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toIsoDay(d);
}

/**
 * The last day the customer can still read the portal after the Enddatum: Enddatum + Nachlauf days
 * (the Dankesbrief's {{ende_portal_nachlauf}}, the admin's «Portalzugang Tage nach Enddatum»).
 * The Enddatum is inclusive, so the 00:05 Zurich run makes the customer inaktiv on Enddatum + 1 and
 * the portal turns lesend from that day for the Nachlauf days: the access runs out at the start of
 * Enddatum + days + 1, and the last full day is this one. Every surface that names the run-out date
 * names this day (an inaktiv run that comes later only adds days).
 */
export function portalNachlaufLastDayIso(endDate: string, nachlaufTage: number): string {
  return addDaysIso(endDate, nachlaufTage);
}

/** `YYYY-MM-DD` plus n calendar years (29 Feb -> 28 Feb in a common year). */
export function addYearsIso(iso: string, years: number): string {
  const d = parseIsoDay(iso);
  const target = new Date(Date.UTC(d.getUTCFullYear() + years, d.getUTCMonth(), 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d.getUTCDate(), lastDay));
  return toIsoDay(target);
}

function endOfMonthIso(iso: string): string {
  const d = parseIsoDay(iso);
  return toIsoDay(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)));
}

function endOfQuarterIso(iso: string): string {
  const d = parseIsoDay(iso);
  const quarterEndMonth = Math.floor(d.getUTCMonth() / 3) * 3 + 3;
  return toIsoDay(new Date(Date.UTC(d.getUTCFullYear(), quarterEndMonth, 0)));
}

/** `YYYY-MM-DD` -> `dd.mm.yyyy` (the date stays the calendar day; no time zone involved). */
export function formatIsoDayDe(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
export function isIsoDay(value: unknown): value is string {
  if (typeof value !== 'string' || !ISO_DAY.test(value)) return false;
  return toIsoDay(parseIsoDay(value)) === value;
}

// ── the Enddatum rule (7.7) ────────────────────────────────────────────────────────────────────

/**
 * The date a Fristregel derives when the cancellation names none (UC6: «abgeleitet»). It counts from
 * the Eingangsdatum (7.7), not from the day of the Freigabe.
 */
export function fristregelEndDate(fristregel: Fristregel, fristregelTage: number, receivedOn: string): string {
  switch (fristregel) {
    case 'sofort':
      return receivedOn;
    case 'monatsende':
      return endOfMonthIso(receivedOn);
    case 'quartalsende':
      return endOfQuarterIso(receivedOn);
    case 'x_tage':
      return addDaysIso(receivedOn, fristregelTage);
  }
}

export interface DeriveEndDateInput {
  art: TerminationArt;
  requestedEndDate?: string | null;
  /** Eingangsdatum (customer, or the date staff enter for a manual one; default today). The reference of the rule. */
  receivedOn: string;
  fristregel: Fristregel;
  fristregelTage: number;
  /** The signed mandate's start (its signed_at as a Swiss day); null for a customer without a mandate. */
  mandateSignedOn: string | null;
  /** Staff entered the date (manual trigger): a named date counts as «erfasst», not «dokument». */
  manual?: boolean;
}

export type EndDateFinding = 'rueckwirkend' | 'vor_mandatsbeginn';

export interface DeriveEndDateResult {
  endDate: string;
  source: EndDateSource;
  finding?: EndDateFinding;
}

/**
 * The Enddatum of a termination (7.7, the five cases):
 *   sofort                      -> the Eingangsdatum, «erfasst»
 *   stichtag with a date         -> that date, «dokument» (customer) / «erfasst» (manual)
 *   stichtag without a date     -> the tenant's Fristregel from the Eingangsdatum, «abgeleitet» (staff confirms, UC6)
 *   a date before the Eingangsdatum -> finding «rueckwirkend» (UC7, 7.6/7.7):
 *                                  the dialog offers «sofort per Eingangsdatum» or the Rückdatierung; the source
 *                                  becomes «rueckdatiert» only when the Freigabe approves the backdate
 *   a date before the mandate's start -> finding «vor_mandatsbeginn» (refused)
 */
export function deriveEndDate(i: DeriveEndDateInput): DeriveEndDateResult {
  let result: DeriveEndDateResult;
  if (i.art === 'sofort') {
    result = { endDate: i.receivedOn, source: 'erfasst' };
  } else if (i.requestedEndDate) {
    result = { endDate: i.requestedEndDate, source: i.manual === true ? 'erfasst' : 'dokument' };
  } else {
    result = { endDate: fristregelEndDate(i.fristregel, i.fristregelTage, i.receivedOn), source: 'abgeleitet' };
  }
  if (i.mandateSignedOn !== null && result.endDate < i.mandateSignedOn) {
    return { ...result, finding: 'vor_mandatsbeginn' };
  }
  if (result.endDate < i.receivedOn) {
    return { ...result, finding: 'rueckwirkend' };
  }
  return result;
}

// ── DTOs ────────────────────────────────────────────────────────────────────────────────────────

export interface TerminationDto {
  id: string;
  subjectType: 'person' | 'firma';
  subjectId: string;
  /** null for a customer without a signed mandate (legacy / import, decision 27.10.2026). */
  mandateId: string | null;
  mandateKind: 'analyse' | 'broker' | null;
  processNumber: string;
  status: TerminationStatus;
  trigger: TerminationTrigger;
  channel: TerminationChannel | null;
  art: TerminationArt | null;
  requestedEndDate: string | null;
  endDate: string | null;
  endDateSource: EndDateSource | null;
  receivedOn: string | null;
  reasonCode: TerminationReasonCode;
  statusBefore: string;
  openedAt: string;
  approvedAt: string | null;
  approvedByAppUserId: string | null;
  effectiveAt: string | null;
  /** the task «Übergabe offener Vorgänge» (read from the task row); null before it exists. */
  handover?: TerminationHandoverDto | null;
  /** what the Freigabe wrote to each policy's courtage window (the Widerruf restores it); [] after it. */
  courtage_windows?: TerminationCourtageWindowEntry[];
  /** The Berechtigungsprüfung of an intake termination (codes only), null when none ran. */
  checkResult?: TerminationCheckResult | null;
  /** The Prüfaufgabe «Kündigung prüfen» of a Befund, and the Freigabe task the intake opened (the header links them). */
  checkTaskId?: string | null;
  freigabeTaskId?: string | null;
  /** P6-S3: the Dankesbrief (null fields before the Freigabe). */
  letter?: TerminationLetterDto;
}

/** the Übergabe task of a termination as the DTO carries it (from the task row). */
export interface TerminationHandoverDto {
  task_id: string;
  status: string;
  completed_at: string | null;
}

/** one entry of mandate_termination.courtage_windows. */
export interface TerminationCourtageWindowEntry {
  policy_id: string;
  /** The courtage_eligible_to the policy had before the Freigabe (a broker may have set one by hand). */
  to_before: string | null;
  /** What the Freigabe wrote: the Enddatum, or the policy's from for a backdated Enddatum. */
  to_set: string;
  /** The task «Courtagefenster prüfen» of a backdated Enddatum. */
  task_id?: string | null;
  /**
   * A window opened in the notice period after the end the Freigabe set: the start written together with
   * `to_set` (to = from). The Widerruf never restores an end before it (the window check).
   */
  from_set?: string | null;
}

/** the system tasks of a termination (process mandate_termination). */
export const TERMINATION_TASK = {
  UEBERGABE: { kind: 'uebergabe', title: 'Übergabe offener Vorgänge' },
  COURTAGEFENSTER: { kind: 'courtagefenster_pruefen', title: 'Courtagefenster prüfen' },
} as const;

/** one item of the Übergabe checklist (ids and a label; no PII beyond what the screen shows anyway). */
export interface UebergabeChecklistItem {
  id: string;
  label: string;
  /** The item's status code (the web shows its German label). */
  status?: string | null;
  /** Vorgänge only: the document kind code (document_kind, e.g. mandat), shown as a label before the number. */
  kind?: string | null;
  /** policies only: the last day of the courtage window. */
  courtage_eligible_to?: string | null;
}

/** GET /tasks/:id/uebergabe - the live checklist next to the Freigabe snapshot. */
export interface UebergabeChecklistDto {
  task_id: string;
  termination_id: string;
  process_number: string | null;
  end_date: string | null;
  task_status: string;
  items: {
    placements: UebergabeChecklistItem[];
    claims: UebergabeChecklistItem[];
    tasks: UebergabeChecklistItem[];
    vorgaenge: UebergabeChecklistItem[];
    policies: UebergabeChecklistItem[];
  };
  /** The counts the Freigabe saw (folgenanalyse), null for a legacy termination. */
  snapshot: { placements_open: number; claims_open: number; tasks_open: number; vorgaenge_open: number; policies: number } | null;
}

/** P6-S3: the Dankesbrief's dispatch state on the termination (19.1; the board reads it). */
export const TERMINATION_LETTER_STATUS = {
  AUSSTEHEND: 'ausstehend',
  VERSANDT: 'versandt',
  ABGESCHLOSSEN: 'abgeschlossen',
  FEHLGESCHLAGEN: 'fehlgeschlagen',
  /** Cancelled by the Widerruf before it left (review H2 / C1). Terminal. */
  STORNIERT: 'storniert',
} as const;
export type TerminationLetterStatus = (typeof TERMINATION_LETTER_STATUS)[keyof typeof TERMINATION_LETTER_STATUS];
export const TERMINATION_LETTER_STATUS_VALUES: readonly TerminationLetterStatus[] = Object.values(TERMINATION_LETTER_STATUS);
export type TerminationLetterChannel = 'portal' | 'email' | 'post' | 'anderer';
/** The channels a broker picks when he marks a failed letter as sent by hand (review round 2 M1). */
export const TERMINATION_LETTER_MANUAL_CHANNELS = ['post', 'email', 'portal', 'anderer'] as const;
export type TerminationLetterManualChannel = (typeof TERMINATION_LETTER_MANUAL_CHANNELS)[number];
/**
 * Why a letter is fehlgeschlagen (0193 letter_failure_reason). versand_unklar: the mail may have gone
 * (a 5xx, a timeout, a send stuck in flight), so a resend needs an explicit confirm.
 */
export const TERMINATION_LETTER_FAILURE_REASONS = ['versand_unklar', 'render_failed', 'document_unreadable'] as const;
export type TerminationLetterFailureReason = (typeof TERMINATION_LETTER_FAILURE_REASONS)[number];

export interface TerminationLetterDto {
  status: TerminationLetterStatus | null;
  channel: TerminationLetterChannel | null;
  /** When the letter left (abgeschlossen); null while it is pending or in flight. */
  sent_at: string | null;
  document_id: string | null;
  communication_id: string | null;
  /** The broker's note when he marked the letter as sent by hand (review round 2 M2). */
  remark: string | null;
  /** Why the letter failed (also kept on a cancelled one); null otherwise. */
  failure_reason: TerminationLetterFailureReason | null;
}

/** P6-S3: a row of the board's Dankesbrief list (the termination plus the customer's name for the link). */
export type TerminationLetterListItemDto = TerminationDto & { subjectName: string | null };

/** P6-S3: the read-only Dankesbrief preview of the Freigabe dialog (concept step 7). */
export interface TerminationLetterPreviewDto {
  /** The text key, e.g. 'P-06.S9-EMAIL-DE-Sie-KMU'. */
  ref: string;
  subject: string;
  body: string;
}

export interface TerminationImpactDto {
  terminationId: string;
  registrations: {
    p5_broker: { live: number; ids: string[] };
    p4_analyse: { live: number; ids: string[] };
  };
  policies: { active: number; courtage_bearing: number; ids: string[] };
  placements_open: number;
  claims_open: number;
  tasks_open: number;
  vorgaenge_open: number;
  /** The running Mahnläufe of the customer (concept step 6): open insurer notifications whose Frist and reminders run. */
  mahnlaeufe_open: number;
  portal: string | null;
  courtage: { ends_on: string | null; policies: number };
  retention_until: string | null;
  /**
   * The preview only (before anything is opened): the tenant's Enddatum rule as the dialog needs it,
   * so a caller without tenant.read still sees «abgeleitet: <date>» and where a Rückdatierung starts.
   */
  end_date_rule?: TerminationEndDateRuleDto;
}

/**
 * The tenant's Enddatum rule for the dialog: the Swiss day of today (the default Eingangsdatum), the
 * Fristregel with its days (the dialog derives from the Eingangsdatum it shows), the date derived from
 * today. An Enddatum before the Eingangsdatum is a Rückdatierung.
 */
export interface TerminationEndDateRuleDto {
  today: string;
  fristregel: Fristregel;
  fristregel_tage: number;
  /** The date the Fristregel derives from today when the Kündigung names none (UC6 «abgeleitet»). */
  derived_end_date: string;
}

/** The termination summary on the «Mandate & Eintragungen» header read model. */
export interface TerminationSummaryDto {
  id: string;
  status: TerminationStatus;
  end_date: string | null;
  art: TerminationArt | null;
  trigger: TerminationTrigger;
  opened_at: string;
  approved_by: string | null;
  /** When it was revoked (a widerrufen one is shown for 30 days after its Widerruf, P6-S3 round 2 M3). */
  revoked_at?: string | null;
}

/** Activity meta events of the Auflösung (Kap. 12). */
export const MANDATE_TERMINATION_EVENTS = {
  OPENED: 'mandate_termination.opened',
  APPROVED: 'mandate_termination.approved',
  REJECTED: 'mandate_termination.rejected',
  REVOKED: 'mandate_termination.revoked',
  COMPLETED: 'mandate_termination.completed',
  /** the Rücknahmemeldung went out to an insurer. */
  WITHDRAWAL_SENT: 'mandate_termination.withdrawal_sent',
  /** the Übergabe task was created. */
  HANDOVER_CREATED: 'mandate_termination.handover_created',
  LETTER_SENT: 'mandate_termination.letter_sent',
  LETTER_FAILED: 'mandate_termination.letter_failed',
  CUSTOMER_STATUS_CHANGED: 'customer.status_changed',
} as const;

/** «gekündigt per 31.03.2027»: the customer chip for a gekündigt customer with its Enddatum. */
export function gekuendigtPerLabel(endDate: string | null): string {
  return endDate ? `gekündigt per ${formatIsoDayDe(endDate)}` : 'gekündigt';
}

/** What a termination ends: the mandate kind, or the customer relationship when it has no mandate. */
export function terminationMandateLabel(kind: 'analyse' | 'broker' | null): string {
  if (kind === 'broker') return 'Brokermandat';
  if (kind === 'analyse') return 'Analysemandat';
  return 'Kundenbeziehung';
}

/**
 * The live termination of a customer ($1 tenant, $2 subject_type, $3 subject_id): the override and the
 * mandate wizard refuse while one exists (decision 27.10.2026).
 */
export const LIVE_TERMINATION_ID_SQL = `SELECT id FROM mandate_termination
   WHERE tenant_id = $1 AND subject_type = $2 AND subject_id = $3
     AND status IN ('eroeffnet','in_pruefung','freigegeben','wirksam')
   LIMIT 1`;

/** The error codes of the Auflösung (ConflictException etc. payload `code`). */
export const TERMINATION_ERROR = {
  /** A Befund is open (in_pruefung): the Freigabe waits for «Berechtigung bestätigt». */
  CHECK_OPEN: 'termination_check_open',
  REMARK_REQUIRED: 'remark_required',
  CUSTOMER_READ_ONLY: 'customer_read_only',
  MANDATE_PROCESS_OPEN: 'mandate_process_open',
  TERMINATION_OPEN: 'termination_open',
  STATUS_ILLEGAL: 'termination_status_illegal',
  BROKER_SUPERSEDES: 'broker_mandate_supersedes',
  DOCUMENT_NOT_FOUND: 'document_not_found',
  END_DATE_BEFORE_MANDATE: 'end_date_before_mandate',
  BACKDATE_REQUIRES_APPROVAL: 'backdate_requires_approval',
  RECEIVED_ON_IN_FUTURE: 'received_on_in_future',
  RECEIVED_ON_TOO_OLD: 'received_on_too_old',
  ILLEGAL_TRANSITION: 'illegal_transition',
  TERMINATION_REQUIRED: 'termination_required',
  NOT_FOUND: 'termination_not_found',
  LETTER_NOT_PENDING: 'letter_not_pending',
  LETTER_NOT_FAILED: 'letter_not_failed',
  LETTER_DOCUMENT_MISSING: 'letter_document_missing',
  /** «Erneut senden» on a letter whose mail may have gone, without the explicit confirm. */
  LETTER_SEND_UNCLEAR: 'letter_send_unclear',
} as const;

/** The hint on a 409 mandate_process_open: an open mandate process is stopped by its Abbruch (UC11). */
export const TERMINATION_HINT_ABBRUCH = 'abbruch_statt_aufloesung';

/** The customer_state row under FOR UPDATE ($1 tenant, $2 subject_type, $3 subject_id): the first lock of every P6 status write. */
export const LOCK_CUSTOMER_STATE_SQL = `SELECT id, status, in_verarbeitung AS "inVerarbeitung", to_char(end_date, 'YYYY-MM-DD') AS "endDate"
      FROM customer_state
     WHERE tenant_id = $1 AND subject_type = $2 AND subject_id = $3
     FOR UPDATE`;

/** Every signed mandate of a customer -> terminated (7.8), fenced per row ($1 tenant, $2 subject_type, $3 subject_id, $4 the end date for a mandate without ends_on, or null). */
export const TERMINATE_SUBJECT_MANDATES_SQL = `UPDATE mandate
        SET status = 'terminated', terminated_at = now(), ends_on = COALESCE(ends_on, $4::date)
      WHERE tenant_id = $1 AND subject_type = $2 AND subject_id = $3 AND status = 'signed'
      RETURNING id, kind`;

// ── Kündigung über Eingangskanäle (Stefan P6 V1.0 steps 1-4) ─────────────────────────────────

/** How the inbound mail found its customer, and the score it routes with (the lower of this and the parse). */
export const TERMINATION_MATCH_SCORES = { reference: 0.95, sender: 0.9 } as const;
export type TerminationMatchMethod = keyof typeof TERMINATION_MATCH_SCORES;

/** The Kundennummer printed on a letter (K-nnnnnn, 0171). The /g regex only through matchAll. */
export const TERMINATION_CODE_SEARCH = { KUNDENNUMMER: /\bK-\d{6}\b/g } as const;

/** The first Kundennummer in a text, or null. */
export function firstKundennummer(text: string | null | undefined): string | null {
  for (const m of (text ?? '').matchAll(TERMINATION_CODE_SEARCH.KUNDENNUMMER)) return m[0];
  return null;
}

export const TERMINATION_PROPOSAL_OUTCOMES = ['kuendigung', 'keine_kuendigung', 'fehler'] as const;
export type TerminationProposalOutcome = (typeof TERMINATION_PROPOSAL_OUTCOMES)[number];
export const TERMINATION_PROPOSAL_DECISIONS = ['uebernommen', 'korrigiert', 'verworfen'] as const;
export type TerminationProposalDecision = (typeof TERMINATION_PROPOSAL_DECISIONS)[number];
export type ParsedMandateKind = 'broker' | 'analyse' | 'unklar';
export type ParsedTerminationArt = 'sofort' | 'stichtag' | 'unklar';

/**
 * The mandate a mail cancels (Kap. 8 row 8): «unklar» or «broker» -> the signed Brokermandat (its end ends
 * the whole relationship); «unklar» with only an Analysemandat -> that one; an explicit «analyse» -> the
 * Analysemandat only when no Brokermandat is signed. null: nothing to open by itself (a human decides).
 */
export function deriveMandateKindFromMail(parsed: ParsedMandateKind, signed: { broker: boolean; analyse: boolean }): 'broker' | 'analyse' | null {
  if (parsed === 'analyse') return signed.analyse && !signed.broker ? 'analyse' : null;
  if (signed.broker) return 'broker';
  if (parsed === 'unklar' && signed.analyse) return 'analyse';
  return null;
}

/** The Berechtigungsprüfung's Befund codes (7.6, UC3, Kap. 8 rows 4-5). */
export const TERMINATION_CHECK_CODES = {
  KEINE_UNTERSCHRIFT: 'keine_unterschrift',
  KEINE_REFERENZ: 'keine_referenz',
  UNTER_SCHWELLE: 'unterschrift_unter_schwelle',
  ZEICHNUNGSREGEL_NICHT_ERFUELLT: 'zeichnungsregel_nicht_erfuellt',
  ZEICHNUNGSREGEL_NICHT_VERIFIZIERT: 'zeichnungsregel_nicht_verifiziert',
  /** The signature driver is the fixture outside tests: nothing was compared (review decision 27.10.). */
  UNTERSCHRIFT_NICHT_GEPRUEFT: 'unterschrift_nicht_geprueft',
  /** The sender is no stored address of the customer, or its authentication fails: never opened by itself. */
  ABSENDER_NICHT_VERIFIZIERT: 'absender_nicht_verifiziert',
} as const;
export type TerminationCheckCode = (typeof TERMINATION_CHECK_CODES)[keyof typeof TERMINATION_CHECK_CODES];

export const TERMINATION_CHECK_CODE_LABEL: Record<TerminationCheckCode, string> = {
  keine_unterschrift: 'Keine Unterschrift',
  keine_referenz: 'Keine Referenzunterschrift hinterlegt',
  unterschrift_unter_schwelle: 'Unterschrift weicht von der Referenz ab',
  zeichnungsregel_nicht_erfuellt: 'Zeichnungsregel nicht erfüllt',
  zeichnungsregel_nicht_verifiziert: 'Zeichnungsregel nicht prüfbar',
  unterschrift_nicht_geprueft: 'Unterschrift nicht geprüft',
  absender_nicht_verifiziert: 'Absender nicht verifiziert',
};

/** mandate_termination.check_result (ids and codes only). */
export interface TerminationCheckResult {
  signature: 'ok' | 'unter_schwelle' | 'keine_unterschrift' | 'keine_referenz' | 'nicht_geprueft';
  signing_rule: 'erfuellt' | 'nicht_erfuellt' | 'nicht_verifiziert' | 'nicht_anwendbar';
  portal_2fa: 'nicht_anwendbar';
  codes: TerminationCheckCode[];
  checked_at: string;
  override?: { by: string | null; at: string; reason_code: string };
}

/** The task titles of the intake (one map, the UI reads the same words). */
export const TERMINATION_INTAKE_TASK = {
  PROPOSAL: { kind: 'termination_proposal', title: 'Mögliche Kündigung prüfen' },
  CHECK: { kind: 'termination_check', title: 'Kündigung prüfen' },
  ZUORDNEN: { kind: 'termination_zuordnen', title: 'Kündigung zuordnen' },
  ENDDATUM: { kind: 'termination_enddatum', title: 'Enddatum anpassen?' },
  ERNEUT: { kind: 'termination_erneut', title: 'Kündigung erneut eingegangen' },
  INAKTIV: { kind: 'termination_inaktiv', title: 'Dokument nach Inaktivierung' },
  ABBRUCH: { kind: 'termination_abbruch', title: 'Abbruch statt Auflösung' },
} as const;
/** The Freigabe task of an eröffnete termination (the S1 open and the intake's Prüfung write the same title). */
export const TERMINATION_FREIGABE_TASK = { kind: 'termination_freigabe', title: 'Mandatsauflösung freigeben' } as const;
export type TerminationIntakeTaskKind = (typeof TERMINATION_INTAKE_TASK)[keyof typeof TERMINATION_INTAKE_TASK]['kind'];

/** The proposal as the task peek reads it (no mail text; the signer names are the customer's own data). */
export interface TerminationProposalDto {
  id: string;
  taskId: string | null;
  subjectType: 'person' | 'firma';
  subjectId: string;
  communicationId: string;
  documentId: string | null;
  mandateKind: ParsedMandateKind;
  art: ParsedTerminationArt;
  endDate: string | null;
  receivedOn: string | null;
  reason: string | null;
  signerNames: string[];
  confidence: number;
  matchMethod: TerminationMatchMethod;
  /** The inbound channel the termination opens with: 'email' today; 'upload' reserved (upload channel not built, follow-up). */
  channel: 'email' | 'upload';
  /** Why the mail was not opened by itself (e.g. absender_nicht_verifiziert); [] when routed by confidence only. */
  reviewReasons: string[];
  terminationId: string | null;
  decision: TerminationProposalDecision | null;
}

/** The Prüfaufgabe's reason codes the «Berechtigung bestätigt» route accepts. */
export const TERMINATION_OVERRIDE_REASON_CODES = ['unterschrift_geprueft', 'vollmacht_vorhanden', 'telefonisch_bestaetigt', 'sonstiges'] as const;
export type TerminationOverrideReasonCode = (typeof TERMINATION_OVERRIDE_REASON_CODES)[number];
